/**
 * Agent-prose markdown renderer — the ONE implementation shared by the
 * widget (live chat bubbles) and admin (Coach bench answers). Lifted
 * verbatim from packages/widget/src/components/markdown.ts, which was a
 * port of legacy `agent-chat-v2/components/Markdown/index.vue`.
 * Block-level: headings, code fences, ordered/unordered lists,
 * blockquote, hr, paragraph. Inline: code, links, bold (** / __),
 * italic (* / _).
 *
 * Output is HTML string — caller injects via `innerHTML` after
 * sanitization. URL sanitizer accepts only http(s)/mailto/tel/sp:/
 * anchor/relative.
 *
 * `renderLink` lets a host swap how anchors render without forking the
 * parser: the widget keeps the default clickable `<a>` (its click
 * handler intercepts `sp:` links), while Coach renders sp:nav links
 * INERT — the operator must see what the visitor would see without the
 * bench ever navigating.
 */

const RE_HEADING = /^(#{1,6})\s+(.+)$/;
const RE_UL_ITEM = /^(\s*)([-*+])\s+(.+)$/;
const RE_OL_ITEM = /^(\s*)(\d+)\.\s+(.+)$/;
const RE_BLOCKQUOTE = /^>\s?(.*)$/;
const RE_HR = /^(?:---+|\*\*\*+|___+)\s*$/;
const RE_INLINE_CODE = /`([^`]+)`/g;
const RE_BOLD_STAR = /\*\*(.+?)\*\*/g;
const RE_BOLD_UNDER = /__(.+?)__/g;
const RE_ITALIC_STAR = /(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g;
const RE_ITALIC_UNDER = /(?<![a-zA-Z0-9])_(?!_)(.+?)(?<!_)_(?![a-zA-Z0-9_])/g;
const RE_LINK = /\[([^\]]+)\]\(([^)]+)\)/g;
const RE_LINE_BREAK = /\n/g;
// `sp:` is the widget's custom scheme for inline agent links (see
// project_inline_sp_nav_links). The widget's click handler in index.tsx
// intercepts these and converts the click to a navigation + agent
// follow-up message. Without this entry the sanitizer dropped sp:
// links to plain text, defeating the entire prompt rule chain.
const SAFE_URL_RE = /^(?:https?:|mailto:|tel:|sp:|#|\/)/i;

type Block =
  | { type: 'heading'; level: number; content: [string] }
  | { type: 'code'; content: string[]; language: string }
  | { type: 'ul' | 'ol'; items: Array<{ indent: number; text: string }> }
  | { type: 'blockquote'; content: string[] }
  | { type: 'hr' }
  | { type: 'paragraph'; content: string[] };

export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function sanitizeUrl(url: string): string {
  return SAFE_URL_RE.test(url.trim()) ? url.trim() : '';
}

/**
 * Custom anchor renderer. Receives the RAW label and the sanitized URL
 * (never called for URLs the sanitizer rejected — those render as plain
 * escaped text). Must return HTML and is responsible for escaping both
 * values (use the exported `escapeHtml`).
 */
export type RenderLink = (label: string, url: string) => string;

const defaultRenderLink: RenderLink = (label, url) =>
  `<a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(label)}</a>`;

export type RenderMarkdownOptions = {
  renderLink?: RenderLink;
};

/**
 * Parse an `sp:` agent-link href into its parts. Mirrors the widget's
 * click handler: `sp:nav?url=/saas&msg=Tell+me+about+SaaS` or
 * `sp:?msg=...`. Returns null when the href isn't an sp: link or
 * carries neither param.
 */
export function parseSpLink(href: string): { url: string; msg: string } | null {
  if (!href.startsWith('sp:')) return null;
  const queryStart = href.indexOf('?');
  if (queryStart === -1) return null;
  const params = new URLSearchParams(href.slice(queryStart + 1));
  const url = params.get('url')?.trim() ?? '';
  const msg = params.get('msg')?.trim() ?? '';
  if (!url && !msg) return null;
  return { url, msg };
}

/**
 * During SSE streaming the buffer can end on an incomplete markdown
 * link, e.g. `... visit [SaaS & Tech](sp:nav?url=/customers/saas-` —
 * the closing `)` hasn't arrived yet. Without this pre-pass the raw
 * URL flashes visible until the next chunk completes the syntax,
 * then the renderer collapses it into a hyperlink. Looks janky.
 *
 * Strip the trailing in-progress link syntax to its bare label so
 * the visitor sees "visit SaaS & Tech" mid-stream, then it morphs
 * into a real link as soon as the closing paren arrives.
 */
function stripIncompleteTrailingLink(md: string): string {
  // Match `[label]` optionally followed by `(`, partial url chars,
  // and NO closing paren. Anchored to end-of-string.
  return md.replace(/\[([^\]\n]*)\](\([^)\n]*)?$/, '$1');
}

export function renderMarkdown(md: string, opts?: RenderMarkdownOptions): string {
  if (!md) return '';
  md = stripIncompleteTrailingLink(md);
  const renderLink = opts?.renderLink ?? defaultRenderLink;
  const lines = md.split('\n');
  const blocks: Block[] = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let cur: any = { type: null, content: [] };

  const flush = (): void => {
    if (cur.type && (cur.content?.length || cur.items?.length)) blocks.push(cur);
    cur = { type: null, content: [] };
  };

  for (const line of lines) {
    const t = line.trim();
    if (t === '') {
      if (cur.type && cur.type !== 'ul' && cur.type !== 'ol') flush();
      continue;
    }
    if (t.startsWith('```')) {
      if (cur.type === 'code') {
        blocks.push(cur);
        cur = { type: null, content: [] };
      } else {
        flush();
        cur = { type: 'code', content: [], language: t.slice(3).trim() };
      }
      continue;
    }
    if (cur.type === 'code') {
      cur.content.push(line);
      continue;
    }
    if (RE_HR.test(t)) {
      flush();
      blocks.push({ type: 'hr' });
      continue;
    }
    const h = line.match(RE_HEADING);
    if (h) {
      flush();
      blocks.push({ type: 'heading', level: h[1]!.length, content: [h[2]!] });
      continue;
    }
    const bq = line.match(RE_BLOCKQUOTE);
    if (bq) {
      if (cur.type !== 'blockquote') {
        flush();
        cur = { type: 'blockquote', content: [] };
      }
      cur.content.push(bq[1]);
      continue;
    }
    const ul = line.match(RE_UL_ITEM);
    if (ul) {
      if (cur.type !== 'ul') {
        flush();
        cur = { type: 'ul', items: [] };
      }
      cur.items.push({ indent: ul[1]!.length, text: ul[3]! });
      continue;
    }
    const ol = line.match(RE_OL_ITEM);
    if (ol) {
      if (cur.type !== 'ol') {
        flush();
        cur = { type: 'ol', items: [] };
      }
      cur.items.push({ indent: ol[1]!.length, text: ol[3]! });
      continue;
    }
    if (cur.type === null || cur.type === 'paragraph') {
      cur.type = 'paragraph';
      cur.content.push(line);
    } else {
      flush();
      cur = { type: 'paragraph', content: [line] };
    }
  }
  if (cur.type && (cur.content?.length || cur.items?.length)) blocks.push(cur);
  return blocks.map((b) => renderBlock(b, renderLink)).join('');
}

function renderBlock(b: Block, renderLink: RenderLink): string {
  switch (b.type) {
    case 'heading':
      return `<h${b.level}>${parseInline(b.content[0], renderLink)}</h${b.level}>`;
    case 'code': {
      const escaped = escapeHtml(b.content.join('\n'));
      const cls = b.language ? ` class="language-${escapeHtml(b.language)}"` : '';
      return `<pre><code${cls}>${escaped}</code></pre>`;
    }
    case 'ul':
      return `<ul>${renderItems(b.items, 'ul', renderLink)}</ul>`;
    case 'ol':
      return `<ol>${renderItems(b.items, 'ol', renderLink)}</ol>`;
    case 'blockquote':
      return `<blockquote><p>${parseInline(b.content.join('\n'), renderLink)}</p></blockquote>`;
    case 'hr':
      return '<hr>';
    case 'paragraph':
      return `<p>${parseInline(b.content.join('\n'), renderLink)}</p>`;
    default:
      return '';
  }
}

function renderItems(
  items: Array<{ indent: number; text: string }>,
  tag: 'ul' | 'ol',
  renderLink: RenderLink,
): string {
  if (!items.length) return '';
  const base = items[0]!.indent;
  let html = '';
  let i = 0;
  while (i < items.length) {
    const it = items[i]!;
    if (it.indent <= base) {
      const kids: Array<{ indent: number; text: string }> = [];
      let j = i + 1;
      while (j < items.length && items[j]!.indent > base) {
        kids.push(items[j]!);
        j++;
      }
      html += kids.length
        ? `<li>${parseInline(it.text, renderLink)}<${tag}>${renderItems(kids, tag, renderLink)}</${tag}></li>`
        : `<li>${parseInline(it.text, renderLink)}</li>`;
      i = j;
    } else {
      html += `<li>${parseInline(it.text, renderLink)}</li>`;
      i++;
    }
  }
  return html;
}

function parseInline(text: string, renderLink: RenderLink): string {
  if (!text) return '';
  // Stash code spans + links so escaping doesn't break their HTML tags.
  const codes: string[] = [];
  let s = text.replace(RE_INLINE_CODE, (_, code: string) => {
    codes.push(`<code>${escapeHtml(code)}</code>`);
    return `\x00C${codes.length - 1}\x00`;
  });
  const links: string[] = [];
  s = s.replace(RE_LINK, (_, label: string, url: string) => {
    const safe = sanitizeUrl(url);
    links.push(safe ? renderLink(label, safe) : escapeHtml(label));
    return `\x00L${links.length - 1}\x00`;
  });
  s = escapeHtml(s);
  // Bold is BANNED in agent chat (prompt rule: emphasis = sp:nav links,
  // never **). The model still occasionally emits it, so enforce the
  // rule mechanically: strip the markers, render the text plain.
  s = s.replace(RE_BOLD_STAR, '$1');
  s = s.replace(RE_BOLD_UNDER, '$1');
  s = s.replace(RE_ITALIC_STAR, '<em>$1</em>');
  s = s.replace(RE_ITALIC_UNDER, '<em>$1</em>');
  s = s.replace(RE_LINE_BREAK, '<br>');
  codes.forEach((html, i) => {
    s = s.replace(`\x00C${i}\x00`, html);
  });
  links.forEach((html, i) => {
    s = s.replace(`\x00L${i}\x00`, html);
  });
  return s;
}
